import { useState, useMemo, useEffect, useCallback } from "react";
// import { useLocation } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, TableMenu, TableColumn, MenuOption } from "../../components/Table";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { DashboardHeader } from "../../components/DashboardHeader";
import { initAllStores } from "../../utils/cache";
import Room from "../../models/Room";
import Bill from "../../Modal/accounting/BillModal";
import { BillData } from "../../types/bill";
import FullPageSpinner from "../../components/FullPageSpinner";
import Order from "../../models/Order";
import User from "../../models/User";
import Hotel from "../../models/Hotel";
import { getGuestRoomBillData } from "../../utils/bill";
import { errorMessage, sendRequest } from "../../utils/api";
import BookedRoom from "../../models/BookedRoom";
import { filterNonReservationRooms } from "../../utils/booking-helper";
import ExtendStayModal from "../../Modal/bookings/ExtendStayModal";
import { PAGINATION_PAGE_NUMBER } from "../../constants";
import { terminal } from "../../utils/user-helper";
import { isEmptyObject } from "../../utils/helper-function";
import { syncBillHistory, syncServices } from "../../utils/sync";

const columns: TableColumn[] = [
  {
    key: "number", label: "ID",
    getClassName: () => "uppercase",
  },
  {
    key: "name", label: "Type",
    getClassName: () => "capitalize",
  },

  // { key: "cashPrice", label: "Rate (₦)" },
  { key: "cashPrice", label: "Rate", responsive: "md" },
  {
    key: "status",
    label: "Status",
    getClassName: (value) =>
      value === "Available"
        ? "text-green-600 font-semibold"
        : "text-red-500 font-semibold",
  },
];

const title = "Rooms";
const categories = ["Available", "Occupied"];

export const Rooms = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const location = useLocation() as { state?: { category: "Occupied" } };

  const [terminalId] = useState(() => terminal());

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null | undefined>(location?.state?.category);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [showBill, setShowBill] = useState(false);
  const [isLateCheckout, setIslateCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState<any>({});
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [roomBill, setRoomBill] = useState<BillData>({
    issuedBy: "",
    orders: [],
    bookings: [],
    discount: 0,
    hotel: {
      id: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      preference: { vatRate: 0, serviceCharge: 0 } as any
    },
    guestName: "",
    billNumber: ""
  });

  useEffect(() => {
    // Run in background after component mounts
    syncServices(["rooms"])
    syncBillHistory({
      orders: [],
      bookedRooms: [],
    });
  }, []);

  const loadFromCache = useCallback(async () => {
    setLoading(true);
    try {
      const cache = await initAllStores();
      // const cachedRooms: Room[] = await cache.getAllBy("rooms", { terminal: terminalId }, "number");

      const getItems = async (): Promise<(Room)[]> => {
        return await cache.getAllBy(
          "rooms", { terminal: terminalId }, "number"
        );
      }

      let cachedRooms = await getItems();

      if (isEmptyObject(cachedRooms)) {
        await syncServices(["rooms"]);
        cachedRooms = await getItems();
      }

      const saved = await cache.getBy("data", { id: "lastSyncTime" });

      if (saved && saved["rooms"]) setLastSyncedAt(new Date(saved["rooms"]));

      setRooms(cachedRooms);
    } catch (err) {
      console.error("Failed to load items from cache:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFromCache();
    // if (location.state?.category) {
    //   setSelectedCategory(location.state.category);
    // }
  }, [loadFromCache,]);

  // Handle Synchronize button click
  const handleSync = async () => {
    try {
      const cache = await initAllStores();
      //const saved = await cache.getBy("settings", { id: "lastSyncTime" });
      //const lastSyncTime = saved?.value || "2024-01-01T00:00:00.000Z";
      const lastSyncTime = "2024-01-01T00:00:00.000Z";
      // Fetch updated items from API
      const data = await sendRequest("POST", `/sync/service?terminal=${terminalId}`, {
        rooms: lastSyncTime,
      });

      // console.log(rooms)
      // Clear and save updated items to cache
      await cache.clear("rooms");
      await cache.saveMany({ rooms: data.rooms });

      // Update last sync timestamp
      const now = new Date().toISOString();
      await cache.updateManyBy("data", ["lastSyncTime"], {
        rooms: now,
      });

      setLastSyncedAt(new Date());
      loadFromCache();
    } catch (err: any) {
      const error = errorMessage(err.status);
      if (error) {
        return openModal(
          error.title,
          error.msg,
          closeModal,
          false, "Close"
        )
      } else {
        console.error("Background fetch error:", err);
      }
    }
  };

  const filteredData = useMemo(() => {
    return rooms
      .map((room) => ({
        ...room,
        status: room.isOccupied ? "Occupied" : "Available",
        cashPrice: room.cashPrice.toLocaleString() as any
      }))
      .filter((room) => {
        const matchesCategory =
          (selectedCategory ?? "All") === "All" ||
          room.status.toLowerCase() === (selectedCategory ?? "").toLowerCase();

        const matchesSearch =
          search === "" ||
          room.name.toLowerCase().includes(search.toLowerCase()) ||
          room.number.toLowerCase().includes(search.toLowerCase());

        return matchesCategory && matchesSearch;
      });
  }, [search, selectedCategory, rooms]);


  const loadRoomBill = async (roomId: string) => {

    const cache = await initAllStores();
    setLoading(true);

    try {
      const bookedRooms = await cache.getAllBy("bookedRooms", { roomId, isUse: true });

      // Filter out reservations using async-safe pattern
      const noReservation: BookedRoom[] = await filterNonReservationRooms(bookedRooms, cache);

      const user: User | null = await cache.getBy("data", { id: "user" });
      const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });

      if (!user || !hotel) {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }

      const {
        guestName, totalDiscount, orders, billNumber
      } = await getGuestRoomBillData(noReservation ?? []);

      setRoomBill({
        issuedBy: user.firstName + " " + user.lastName,
        orders, discount: totalDiscount,
        bookings: noReservation ?? [],
        hotel, guestName, billNumber
      });

    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }

  const confirmCheckoutModal = async (room: Room) => {
    const cache = await initAllStores();

    const bookedRooms = await cache.getAllBy("bookedRooms", { roomId: room.id, isUse: true });
    const noReservation: BookedRoom[] = await filterNonReservationRooms(bookedRooms, cache);
    const { guestName, totalDiscount, totalPendingAmount } = await getGuestRoomBillData(noReservation);
    // const billData = {
    //   guestName, roomNumber: room.number, totalDiscount,
    //   totalAmount: totalPendingAmount - totalDiscount,
    // };
    const pendingAmount = totalPendingAmount - totalDiscount;
    openModal(
      "Confirm Guest Checkout ⚠️",
      `You're about to check out a guest:
      --------------------------------------------
      Guest: ${guestName}
      Room No: ${room.number}
      Pending Bills: ₦${(pendingAmount < 0 ? 0 : pendingAmount).toLocaleString()}
      ---------------------------------------------
      Once confirmed, all unpaid bills and bookings for this guest will be marked as paid and closed.`,
      () => checkoutGuest(room.id),
      true,
      "Yes, Check Out",
      "Cancel", "red"
    );
  }

  const checkoutGuest = async (roomId: string) => {
    setLoading(true);
    try {
      const cache = await initAllStores();
      await sendRequest("PUT", `/rooms/${roomId}/checkout`);
      const bookedRooms: BookedRoom[] = await cache.getAllBy("bookedRooms", { roomId });

      for (const roomBooking of bookedRooms) {
        const orders: Order[] | null = await cache.getAllBy("orders", { bookedRoomId: roomBooking.id });
        const user = await cache.getBy("data", { id: "user" });
        const userId = user.userId;

        for (const order of orders) {
          await cache.updateManyBy(
            "orders", [order.id], {
            isPaid: true,
            ...(order.isPaid === true)
              ? { clearedById: userId } : {}
          }
          );
        }
      }

      await Promise.all([
        cache.updateManyBy("bookedRooms", bookedRooms.map(b => b.id), { isUse: false }),
        cache.updateManyBy("rooms", [roomId], {
          bookedRooms: [], isOccupied: false,
          passKey: undefined, headline: undefined,
        })
      ]);

      showToast("Guest Checked Out Successfully!", { type: "success" });

      setRooms((prev) =>
        prev.map((it) =>
          it.id === roomId
            ? {
              ...it,
              isOccupied: false,
              headline: undefined,
              passKey: undefined
            }
            : it
        )
      );

    } catch (error: any) {
      const err = errorMessage(error.status);
      if (error.status === 409) {

      } else if (err) {
        return openModal(err.title, err.msg, closeModal, false, "Close");
      }

      console.log(error)
    } finally {
      setLoading(false);
    }
  }

  // const lateCheckout = async (bookingId: string) => {
  //   setLoading(true);
  //   try {
  //     const cache = await initAllStores();

  //     const booking: Booking | null = await cache.getBy("bookings", { id: bookingId });
  //     if (!booking) return;

  //     const refNo = generateReference(
  //       paymentMethod.toLocaleLowerCase() as PaymentType, "booking"
  //     );

  //     // Prepare main booking objects
  //     const bookingData = {
  //       guestData: {
  //         id: booking.customerId,
  //       },
  //       billData: { id: booking.billId },
  //       bookingData: { refNo, id: bookingId, },
  //     };

  //     // Send booking request
  //     await sendRequest("POST", "/rooms/book", { ...bookingData, bookedRoomData });

  //     await Promise.all([
  //       cache.updateManyBy("bookedRooms", bookedRooms.map(b => b.id), { isUse: false }),
  //       cache.updateManyBy("rooms", [roomId], { bookedRooms: [], isOccupied: false })
  //     ]);

  //     showToast("Guest Checked Out Successfully!", { type: "success" });



  //   } catch (error: any) {
  //     const err = errorMessage(error.status);
  //     if (error.status === 409) {

  //     } else if (err) {
  //       return openModal(err.title, err.msg, closeModal, false, "Close");
  //     }

  //     console.log(error)
  //   } finally {
  //     setLoading(false);
  //   }
  // }
  return (
    <div className="p-6">
      <DashboardHeader
        title={title}
        categories={categories}
        search={search}
        setSearch={setSearch}
        onCategorySelect={setSelectedCategory}
        onSync={() => {
          setSearch("");
          handleSync();
        }}
        lastSyncedAt={lastSyncedAt}
      />

      <FullPageSpinner show={loading} />

      {/* Bill Modal */}
      {showBill && (
        <Bill
          hotel={roomBill.hotel}
          discount={roomBill.discount}
          guestName={roomBill.guestName}
          issuedBy={roomBill.issuedBy}
          bookings={roomBill.bookings ?? []}
          orders={roomBill.orders}
          onClose={() => setShowBill(false)}
          billNumber={roomBill.billNumber ?? "BL-UNKNOWN"}
        />
      )}

      {showExtendModal && (
        <ExtendStayModal
          guest={bookingData}
          onClose={() => setShowExtendModal(false)}
          onExtend={(newBooking) => {
            // console.log("Extended stay saved:", newBooking);
            // e.g. update local state or refresh UI
            // setBookedRooms((prev) => [...prev, newBooking]);
          }}
          isLateCheckout={isLateCheckout}
        />
      )}

      {filteredData.length === 0 ? (
        <div className="text-center mt-10 text-gray-500">
          {rooms.length === 0 ? (
            <p>No rooms available.</p>
          ) : (
            <p>No rooms match your search.</p>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          data={filteredData}
          rowsPerPage={PAGINATION_PAGE_NUMBER}
          tableMenu={(row: Room, closeMenu) => (
            <TableMenu
              row={row}
              options={[
                {
                  label: "Room Details",
                  color: "text-blue-500",
                  onClick: (r: Room) => {
                    // Build the room details string
                    let details = `-------------------------------------------
Number: ${r.number}
Type: ${r.name}
Rate: ₦${r.cashPrice.toLocaleString()}
Status: ${r.isOccupied ? "❌ Occupied" : "✅ Available"}
-------------------------------------------`;

                    // Add access code only if it exists
                    if (r.passKey) {
                      details = details.replace(
                        "-------------------------------------------",
                        `Access Code: ${r.passKey}\n-------------------------------------------`
                      );
                    }

                    openModal(
                      "Room Details",
                      details,
                      closeModal,
                      false,
                      "Close"
                    );
                  },
                },
                row.isOccupied
                  ? {
                    label: "Room Bill",
                    color: "text-blue-500",
                    onClick: (r: Room) => {
                      loadRoomBill(r.id)
                      setShowBill(true)
                    },
                  }
                  : null,
                !row.isOccupied
                  ? {
                    label: "Book Room",
                    color: "text-blue-500",
                    onClick: (r: Room) => navigate(`/rooms/book?roomId=${r.id}`),
                  }
                  : null,
                {
                  label: "Reserve Room",
                  color: "text-blue-500",
                  onClick: (r: Room) => {
                    navigate(`/rooms/reserve?roomId=${r.id}`)
                  },
                },
                row.isOccupied
                  ? {
                    label: "Late Checkout",
                    color: "text-blue-500",
                    onClick: async (r: any) => {
                      const cache = await initAllStores();
                      const bookedRoom = await cache.getBy(
                        "bookedRooms", {
                        roomId: r.id,
                        isUse: true,
                      });

                      setBookingData({
                        guestName: bookedRoom?.guestName,
                        roomNo: r?.number,
                        roomId: r.id,
                        bookingId: bookedRoom?.bookingId,
                        roomRate: parseFloat(r.cashPrice.replace(",", "")),
                      });
                      setIslateCheckout(true);
                      setShowExtendModal(true);
                      // const cache = await initAllStores();
                      // const bookedRoom = await cache.getBy(
                      //   "bookedRooms", {
                      //   roomId: r.id, isUse: true,
                      //   bookingType: "fullTime"
                      // });

                      // const hotel: Hotel = JSON.parse(
                      //   localStorage.getItem("hotel") ?? "{}"
                      // );

                      // const lateAmount = (
                      //   hotel.preference.lateCheckoutAmount
                      //   ?? LATE_CHECKOUT_AMOUNT
                      // );
                      // return openModal(
                      //   "Late Checkout Request 🕒",
                      //   `${bookedRoom.guestName}'s stay will be extended for a late checkout `
                      //   + `at an additional charge of ₦${lateAmount}. `
                      //   + "Do you wish to proceed?",
                      //   () => lateCheckout(bookedRoom.bookingId),
                      //   true, "Yes, Proceed"
                      // );
                    },

                  }
                  : null,
                row.isOccupied
                  ? {
                    label: "Extend Stay",
                    color: "text-blue-500",
                    onClick: async (r: any) => {
                      const cache = await initAllStores();
                      const bookedRoom = await cache.getBy(
                        "bookedRooms", {
                        roomId: r.id,
                        bookingType: "fullTime"
                      });

                      if (!bookedRoom) {
                        return openModal(
                          "Extension Restricted ⚠️",
                          "Only Full-time bookings are eligible for stay extension. Kindly reach out to the receptionist for further assistance.",
                          () => { },
                          false,
                          "Close"
                        );
                      }
                      setBookingData({
                        guestName: bookedRoom?.guestName,
                        roomNo: r.number,
                        roomId: r.id,
                        bookingId: bookedRoom.bookingId,
                        roomRate: parseFloat(r.cashPrice.replace(",", "")),
                      });
                      setIslateCheckout(false);
                      setShowExtendModal(true);
                    },
                  }
                  : null,

                row.isOccupied
                  ? {
                    label: "Checkout",
                    color: "text-red-500",
                    onClick: (r: Room) => {
                      confirmCheckoutModal(r)
                    },
                  }
                  : null,
              ].filter(Boolean) as MenuOption[]}
              closeMenu={closeMenu}
            />
          )}
        />
      )}
    </div>
  );
};

